/**
 * MIRROR OF THE SERVER CONTRACT — HAND-EDITED, on purpose.
 *
 * Source of truth: `server/src/api/rag-playground/rag-playground.contract.ts`.
 * Do NOT run `bun sync:contracts` to refresh this file — that generator is
 * destructive in this repo and is banned. When the server contract changes,
 * port the change here by hand, then run `bun run openapi:gen` (and restart
 * Vite) if the change altered a request/response SHAPE.
 *
 * The mirror exists so the web SPA can import zod schemas + oRPC contract types
 * from `@event-manager/api-client/contracts/rag-playground.contract` without
 * reaching into the server workspace — and its ONLY value is being faithful. A
 * silently drifted mirror is worse than no mirror: the FE type-checks against a
 * shape the server does not send.
 */

/**
 * RAG Playground Contract
 *
 * Organization-scoped contract for the `/playground` page. Reads/runs require
 * AI-assistant view access; document mutations require edit access.
 *
 * All documents in this namespace are stored with sourceType='PLAYGROUND'
 * so they are excluded from the production RAG index (agent retrieval,
 * main config page, etc.). Settings passed on each request — embedding
 * model, strategy, reranking — apply ONLY to that request and never mutate
 * the org's saved RAG settings.
 *
 * @module contracts/rag-playground
 */

import { eventIterator, oc } from "@orpc/contract";
import * as z from "zod";
import { AgentWorkflowStateSchema } from "./agent-workflow.contract";
import {
	RagDocumentSchema,
	RagDocumentStatusSchema,
} from "./rag-documents.contract";

// Re-export shared constants so callers can import everything from one place.
export {
	DemoSearchResultSchema,
	EmbeddingModelSchema,
	MAX_RAG_FILE_BYTES,
	RAG_ALLOWED_MIME_TYPES,
	RagDocumentSchema,
	RagDocumentStatusSchema,
} from "./rag-documents.contract";

// Per-request override schemas
export const PlaygroundEmbeddingStrategySchema = z.enum([
	"standard",
	"contextual",
	"late",
]);
export type PlaygroundEmbeddingStrategy = z.infer<
	typeof PlaygroundEmbeddingStrategySchema
>;

export const PlaygroundRerankingModeSchema = z.enum(["off", "on"]);
export type PlaygroundRerankingMode = z.infer<
	typeof PlaygroundRerankingModeSchema
>;

/**
 * Snapshot of a single event in a playground panel's sandbox calendar. Sent
 * from the client on every agent run so the server's calendar tools have a
 * mutable scratch to schedule into. Server returns the same shape inside
 * `tool-output-available` chunks; client mirrors mutations into its store.
 */
export const PlaygroundCalendarEventSchema = z.object({
	id: z.number().int().min(1),
	eventDateTime: z
		.string()
		.describe("Local ISO datetime (YYYY-MM-DDTHH:mm:ss) without timezone"),
	type: z.string().min(1).describe('Event type ID (e.g. "CHECK_UP")'),
	status: z
		.enum(["active", "cancelled"])
		.describe(
			'Sandbox event status. No "completed" — playground never advances time.'
		),
	userName: z.string().optional(),
	phoneE164: z.string().optional(),
});
export type PlaygroundCalendarEvent = z.infer<
	typeof PlaygroundCalendarEventSchema
>;

const PlaygroundChunkSchema = z.object({
	id: z.number().int(),
	chunkIndex: z.number().int(),
	content: z.string(),
	tokenCount: z.number().int().nullable(),
	contextPrefix: z.string().nullable(),
	pageNumber: z.number().int().nullable(),
	sheetName: z.string().nullable(),
	rowRange: z.string().nullable(),
	chunkType: z.enum(["TEXT", "TABLE", "MIXED", "SECTION", "CODE_BLOCK"]),
	embeddingDimensions: z.number().int().nullable(),
	metadata: z.unknown().nullable(),
	createdAt: z.string().datetime(),
});

// ---------------------------------------------------------------------------
// R7 — per-run answering-mode override
// ---------------------------------------------------------------------------

/**
 * The answering mode a single playground run executes under.
 *
 * Same vocabulary as the org-persisted `ragAgentMode` — deliberately the SAME
 * three values, because the sandbox exists to preview what production would do:
 *  - `'1'` single agent (the token-streaming path),
 *  - `'4'` researcher → evidence pack → main agent,
 *  - `'5'` `'4'` + the verifier loop.
 *
 * Sent per RUN. It never touches the org's saved config — this is a sandbox
 * override, exactly like the per-request embedding model / threshold already on
 * this surface. Omitted ⇒ the run uses whatever the org/preset has persisted.
 */
export const PlaygroundAgentModeSchema = z.enum(["1", "4", "5"]);
export type PlaygroundAgentMode = z.infer<typeof PlaygroundAgentModeSchema>;

/**
 * Run-input fields modes 4/5 CANNOT honor, reported verbatim on the `mode-hint`
 * chunk so the UI can grey the corresponding controls instead of pretending they
 * applied.
 *
 * `embeddingModel` steers only mode 1's prompt-context pre-retrieval. Modes 4/5
 * have no such step: the researcher's tools embed the query in each document
 * group's own model. Every other knob applies — `topK` / `threshold` tune the
 * retrieval tools, `maxOutputTokens` / `verbosity` the main agent.
 */
export const PLAYGROUND_MULTI_AGENT_IGNORED_SETTINGS = [
	"embeddingModel",
] as const;

// ---------------------------------------------------------------------------
// R8 — turn-local citation payload
//
// Mirrors `TurnCitationSchema` in
// `server/src/domain/entities/rag/citation-marker.ts`. Kept here rather than in
// `message-citations.contract.ts` because it is a DIFFERENT thing: that contract
// describes PERSISTED, thread-stable citations; these are provisional markers
// that exist for one turn and are never written anywhere.
// ---------------------------------------------------------------------------

/**
 * Why a cited span carries no page/bbox geometry. Explicit and self-describing
 * on purpose: a bare `null` target forces the UI to guess whether geometry is
 * missing, impossible, or merely unmatched — three different things to render.
 *
 *  - `format_has_no_page_model` — the source format has no fixed page model at
 *    all (a Word document: docling's Word backend produces no page number and no
 *    bounding box). A page number here would be fabricated, so none is ever
 *    produced and the UI must NOT route the span into a page/PDF view; the
 *    breadcrumb (falling back to title, then filename) is the honest location.
 *  - `no_layout` — no layout artifact was stored (text, markdown, spreadsheet
 *    and image parsers produce none).
 *  - `quote_not_located` — a layout exists but the quote matched no element in
 *    it (normal flow: rewritten quote, table cell, OCR drift).
 *  - `no_document` — the span was registered without a document id.
 *  - `resolution_failed` — the provenance read itself failed; geometry is
 *    UNKNOWN rather than known-absent. Never render this as "not found".
 */
export const PlaygroundCitationNoGeometryReasonSchema = z.enum([
	"format_has_no_page_model",
	"no_layout",
	"quote_not_located",
	"no_document",
	"resolution_failed",
]);
export type PlaygroundCitationNoGeometryReason = z.infer<
	typeof PlaygroundCitationNoGeometryReasonSchema
>;

/**
 * What a resolved citation points at. `pdf_bbox` is the only kind carrying
 * geometry; `no_geometry` is a first-class outcome, not a failure. Branch on
 * THIS, never on `target === null`.
 */
export const PlaygroundCitationGeometryKindSchema = z.enum([
	"pdf_bbox",
	"no_geometry",
]);

/** One element box in docling page-space (NOT normalized). */
export const PlaygroundCitationBBoxSchema = z.object({
	b: z.number(),
	/** 'BOTTOMLEFT' | 'TOPLEFT' — the renderer flips per-box against page height. */
	coordOrigin: z.string(),
	l: z.number(),
	pageNo: z.number(),
	r: z.number(),
	t: z.number(),
});

export const PlaygroundCitationPageSizeSchema = z.object({
	height: z.number(),
	width: z.number(),
});

/** `pdf_bbox` geometry: page + boxes + per-page sizes for the origin flip. */
export const PlaygroundCitationPdfBboxTargetSchema = z.object({
	bboxes: z.array(PlaygroundCitationBBoxSchema),
	pageNo: z.number().nullable(),
	/** Keyed by page number as a STRING ("3") — JSON object keys always are. */
	pageSizes: z.record(z.string(), PlaygroundCitationPageSizeSchema),
});

/**
 * One cited span, resolved but NOT persisted.
 *
 * `displayNumber` / `marker` are TURN-LOCAL: the provisional numbers the model
 * was shown this turn (`S1`, `S2`, … in first-seen order), and they RESTART
 * every turn because nothing here touches the per-conversation registry. That is
 * what `numbering: 'turn_local'` states on the wire — a consumer must label
 * these as sandbox markers and must not imply parity with the thread-stable
 * numbering of persisted chat citations.
 */
export const PlaygroundTurnCitationSchema = z.object({
	/** Heading ancestry of the cited chunk; the honest location for a .docx. */
	breadcrumb: z.string().nullable(),
	chunkId: z.string().nullable(),
	/** Turn-local number; `marker` is `S<displayNumber>`. */
	displayNumber: z.number().int().positive(),
	documentId: z.string().nullable(),
	filename: z.string().nullable(),
	/** Bare marker token the model was shown, e.g. `"S1"` (rendered `⟦S1⟧`). */
	marker: z.string(),
	/** Set exactly when `targetKind === 'no_geometry'`; null otherwise. */
	noGeometryReason: PlaygroundCitationNoGeometryReasonSchema.nullable(),
	/** Numbering provenance. Turn-local always — this payload never persists. */
	numbering: z.literal("turn_local"),
	quote: z.string(),
	/** Present only for `pdf_bbox`; null for every no-geometry span. */
	target: PlaygroundCitationPdfBboxTargetSchema.nullable(),
	targetKind: PlaygroundCitationGeometryKindSchema,
	/** Section title of the cited chunk (last breadcrumb segment). */
	title: z.string().nullable(),
});
export type PlaygroundTurnCitation = z.infer<
	typeof PlaygroundTurnCitationSchema
>;

// ---------------------------------------------------------------------------
// agentRunStream SSE chunk types added after the original union shipped.
//
// Every one of them is a NEW member of the discriminated union rather than a new
// required field on an existing member, so a client built against the older
// contract simply drops the frames it does not recognise.
// ---------------------------------------------------------------------------

/**
 * Terminal usage + diagnostics chunk.
 *
 * THE ORIGINAL MIRROR OF THIS CHUNK CARRIED ONLY THE FIRST FOUR NUMBERS while
 * the server had long been sending the rest — the FE read fields this file said
 * did not exist. Everything after `latencyMs` is OPTIONAL because the SOURCE is
 * optional, not because it is unimportant: mode 1 fills them from the SDK's
 * `finish-step` / `finish` parts, and modes 4/5 mostly cannot fill them at all
 * (the pipeline is non-streaming, so there are no per-step timings, no
 * `finishReason` and no throughput to report).
 *
 * A consumer MUST treat every absent optional as genuinely unmeasured and render
 * it as such. A missing throughput is not "0 tok/s"; a missing `stepCount` is
 * not "0 steps".
 */
export const PlaygroundFinishChunkSchema = z.object({
	type: z.literal("finish"),
	promptTokens: z.number().int(),
	completionTokens: z.number().int(),
	totalTokens: z.number().int(),
	latencyMs: z.number().int(),
	stepCount: z.number().int().optional(),
	toolCallCount: z.number().int().optional(),
	toolErrorCount: z.number().int().optional(),
	/**
	 * Mode 1 only. Modes 4/5 use up to THREE models (main, researcher,
	 * verifier); there is no single answer, so the field is omitted rather than
	 * misreported — fall back to `start.model` and read the rest off `mode-hint`.
	 */
	modelId: z.string().optional(),
	finishReason: z.string().optional(),
	reasoningTokens: z.number().int().optional(),
	cachedInputTokens: z.number().int().optional(),
	textOutputTokens: z.number().int().optional(),
	timeToFirstOutputMs: z.number().optional(),
	outputTokensPerSecond: z.number().optional(),
	effectiveOutputTokensPerSecond: z.number().optional(),
	responseTimeMs: z.number().optional(),
	toolExecutionTotalMs: z.number().optional(),
	/** Which answering mode produced these numbers. */
	agentMode: PlaygroundAgentModeSchema.optional(),
	/** Number of verifier passes that actually ran (mode 5). Absent on modes 1/4. */
	verifierLoops: z.number().int().min(0).optional(),
});
export type PlaygroundFinishChunk = z.infer<typeof PlaygroundFinishChunkSchema>;

/**
 * R7 — what the run is actually doing. The FIRST frame of every authorized run,
 * ahead of `start`.
 *
 * Ahead of `start` on purpose: this chunk depends only on the resolved mode and
 * the run's tool set, neither of which can fail, while mode 1's `start` needs a
 * preparation step that can (no AI provider, embedding error). Emitted second it
 * would have gone missing precisely on the runs that error out — the ones where
 * the operator most needs to know which mode ran. So a client may key its panel
 * label off this frame and expect it before anything else.
 *
 * Present on EVERY run (mode 1 included) so the UI never has to infer the
 * effective mode from the absence of other chunks. Modes 4/5 stream the answer
 * only after the researcher has run, so the `agent-workflow` tracker is the
 * live signal until then.
 */
export const PlaygroundModeHintChunkSchema = z.object({
	type: z.literal("mode-hint"),
	/** The mode this run executes under (override applied). */
	agentMode: PlaygroundAgentModeSchema,
	/** The mode the org/preset has persisted — what production would do today. */
	configuredMode: PlaygroundAgentModeSchema,
	/** True when `agentMode !== configuredMode`, i.e. this run is a sandbox override. */
	overridden: z.boolean(),
	/**
	 * False on mode 5: its answer streams only when the verifier loop cannot
	 * replace it, otherwise it arrives whole in one `text-delta`.
	 */
	streamsTokens: z.boolean(),
	/**
	 * What `start.finalPrompt` actually IS on this run — machine-readable so a
	 * client never mislabels it.
	 *  - `'model_input'` (mode 1): the exact system + user text handed to the
	 *    model, retrieved-context block included.
	 *  - `'pipeline_input'` (modes 4/5): the system prompt + question handed to
	 *    the multi-agent pipeline. The prompts the researcher / main / verifier
	 *    models actually receive are assembled INSIDE the pipeline (research
	 *    brief, evidence pack, revision feedback) and are not observable from the
	 *    playground, so a UI must not label this "what the model saw".
	 */
	finalPromptKind: z.enum(["model_input", "pipeline_input"]),
	/**
	 * Run-input settings this mode cannot honor — see
	 * {@link PLAYGROUND_MULTI_AGENT_IGNORED_SETTINGS}. Empty on mode 1. Grey the
	 * matching controls; do not show them as applied.
	 */
	ignoredSettings: z.array(z.string()),
	/**
	 * Enabled tools no agent of this run holds. Modes 4/5 give the researcher the
	 * retrieval tools and the main agent every other tool.
	 */
	inactiveTools: z.array(z.string()),
	/**
	 * Researcher model for this run; `null` ⇒ the main model is reused. Always
	 * `null` on mode 1 (there is no researcher).
	 */
	researcherModel: z.string().nullable(),
	/**
	 * Verifier model (mode 5): the org's, or the server default when the org
	 * configured none. `null` on modes 1/4.
	 */
	verifierModel: z.string().nullable(),
	/** Verifier loop cap (mode 5). */
	verifierMaxLoops: z.number().int().optional(),
});
export type PlaygroundModeHintChunk = z.infer<
	typeof PlaygroundModeHintChunkSchema
>;

/**
 * R7 — live multi-agent step tracker (research → draft → verify → revise).
 *
 * The WHOLE state on every transition (steps are few), so a consumer REPLACES
 * rather than merges and a late/duplicate frame is harmless. Only modes 4/5 emit
 * it; mode 1 has no pipeline and therefore no tracker.
 */
export const PlaygroundAgentWorkflowChunkSchema = z.object({
	type: z.literal("agent-workflow"),
	workflow: AgentWorkflowStateSchema,
});
export type PlaygroundAgentWorkflowChunk = z.infer<
	typeof PlaygroundAgentWorkflowChunkSchema
>;

/**
 * R8 — the turn's cited spans, resolved but NEVER persisted.
 *
 * `numbering: 'turn_local'` is on the wire because it is load-bearing: these
 * markers are the provisional `S<n>` the model was shown THIS turn and they
 * restart every turn. There is no registry, no `message_citation` row and no
 * finalize on this surface, so a consumer MUST label them as sandbox markers and
 * must not imply parity with persisted chat citation numbering.
 *
 * Emitted once, after generation, before `finish`. An EMPTY array is a real
 * answer ("nothing was cited"); the chunk being absent means something else
 * entirely (the run had no citation capability at all).
 */
export const PlaygroundCitationsChunkSchema = z.object({
	type: z.literal("citations"),
	numbering: z.literal("turn_local"),
	citations: z.array(PlaygroundTurnCitationSchema),
});
export type PlaygroundCitationsChunk = z.infer<
	typeof PlaygroundCitationsChunkSchema
>;

/**
 * Every frame `agentRunStream` can put on the wire — the single union the FE
 * should switch on.
 *
 * Hoisted out of the `.output(eventIterator(...))` position so a consumer can
 * import it directly (parse a raw SSE frame, exhaustively narrow a reducer)
 * instead of digging the shape out of the oRPC contract type.
 *
 * Discriminated on `type`. Chunk types added after a client shipped are simply
 * dropped by that client's parser, which is why every addition here is a NEW
 * member rather than a new required field on an existing one.
 */
export const PlaygroundAgentStreamChunkSchema = z.discriminatedUnion("type", [
	// Sent once at the very start (after any pre-retrieval) so the client can
	// attach `retrievedChunks` to the in-flight assistant turn before text.
	z.object({
		type: z.literal("start"),
		model: z.string(),
		retrievedChunks: z.array(
			z.object({
				documentId: z.number().int(),
				filename: z.string(),
				content: z.string(),
				similarity: z.number(),
				marker: z.string().optional(),
			})
		),
		finalPrompt: z.string(),
		queryEmbeddingTokens: z.number().int(),
		warning: z.string().optional(),
		/**
		 * R7 — the effective mode. Modes 4/5 run no pre-retrieval, so
		 * `retrievedChunks` is `[]` and `queryEmbeddingTokens` is 0 on those
		 * runs: that is "no retrieval happened here", NOT "retrieval found
		 * nothing". See the `mode-hint` chunk.
		 */
		agentMode: PlaygroundAgentModeSchema.optional(),
	}),
	PlaygroundModeHintChunkSchema,
	// Incremental text deltas. On modes 4/5 there is exactly ONE of these,
	// carrying the whole finished answer — the pipeline does not token-stream
	// and nothing is simulated.
	z.object({
		type: z.literal("text-delta"),
		delta: z.string(),
	}),
	// Optional reasoning stream for reasoning-capable models.
	z.object({
		type: z.literal("reasoning-delta"),
		delta: z.string(),
	}),
	// Tool-call lifecycle. Mirrors the AI SDK fullStream part shapes so the
	// client can convert them into UIMessagePart tool entries with minimal
	// transformation. Modes 4/5 emit only the terminal pair
	// (`tool-input-available` + output/error): the researcher's tool activity is
	// reported once the pipeline finishes, so no `-start` / `-delta` is
	// synthesised — inventing the lifecycle would make a batch result look like
	// a stream.
	z.object({
		type: z.literal("tool-input-start"),
		toolCallId: z.string(),
		toolName: z.string(),
	}),
	z.object({
		type: z.literal("tool-input-delta"),
		toolCallId: z.string(),
		delta: z.string(),
	}),
	z.object({
		type: z.literal("tool-input-available"),
		toolCallId: z.string(),
		toolName: z.string(),
		input: z.unknown(),
	}),
	z.object({
		type: z.literal("tool-output-available"),
		toolCallId: z.string(),
		toolName: z.string(),
		output: z.unknown(),
	}),
	z.object({
		type: z.literal("tool-error"),
		toolCallId: z.string(),
		toolName: z.string(),
		errorText: z.string(),
	}),
	// Approval pending: server omitted `execute` for this tool because its
	// needsApproval is true. No output will arrive in this stream; the client
	// must surface Approve/Deny and re-issue with `toolApprovals` populated.
	z.object({
		type: z.literal("tool-approval-required"),
		toolCallId: z.string(),
		toolName: z.string(),
		input: z.unknown(),
	}),
	// Calendar mutation summary — emitted by calendar tools alongside their
	// `tool-output-available` chunk so the client can commit the change into its
	// panel store without re-parsing tool output shapes.
	z.object({
		type: z.literal("calendar-mutation"),
		op: z.enum(["create", "update", "cancel"]),
		event: PlaygroundCalendarEventSchema,
	}),
	PlaygroundAgentWorkflowChunkSchema,
	PlaygroundCitationsChunkSchema,
	PlaygroundFinishChunkSchema,
	// Recoverable error mid-stream. Terminates the stream.
	z.object({
		type: z.literal("error"),
		errorText: z.string(),
	}),
]);
export type PlaygroundAgentStreamChunk = z.infer<
	typeof PlaygroundAgentStreamChunkSchema
>;

export const ragPlaygroundContract = {
	/**
	 * Upload a document into the isolated playground store. The created
	 * RagDocument row carries sourceType='PLAYGROUND' and is invisible to
	 * the production RAG retrieval paths. The `embeddingStrategy` chosen
	 * here is recorded on the document so the indexing pipeline knows which
	 * chunker to run.
	 */
	upload: oc
		.route({
			method: "POST",
			path: "/api/playground/rag/documents/upload",
			summary: "Upload document into the playground RAG index",
			tags: ["Playground"],
		})
		.input(
			z.object({
				file: z.file(),
				embeddingStrategy: PlaygroundEmbeddingStrategySchema.optional(),
				embeddingModel: z
					.string()
					.optional()
					.describe(
						"Accepted on the wire but NOT consumed: RagPlaygroundService.upload takes only { file, embeddingStrategy }. The ingest embeds with the org/default model, so a UI must not claim the sandbox ingest used a user-chosen embedding model."
					),
			})
		)
		.output(
			z.object({
				id: z.number().int(),
				filename: z.string(),
				status: RagDocumentStatusSchema,
			})
		),

	/** List playground documents (sourceType='PLAYGROUND'). */
	list: oc
		.route({
			method: "GET",
			path: "/api/playground/rag/documents",
			summary: "List playground RAG documents",
			tags: ["Playground"],
		})
		.input(
			z.object({
				limit: z.coerce.number().int().min(1).max(100).optional().default(50),
				cursor: z.coerce.number().int().min(0).optional().default(0),
			})
		)
		.output(
			z.object({
				documents: z.array(RagDocumentSchema),
				total: z.number().int(),
			})
		),

	/** Delete a playground document. Hard-fails if the document is not
	 *  scoped to the playground, so a tampered id can't drop production
	 *  data. */
	delete: oc
		.route({
			method: "DELETE",
			path: "/api/playground/rag/documents/{id}",
			summary: "Delete a playground document",
			tags: ["Playground"],
		})
		.input(z.object({ id: z.coerce.number().int().min(1) }))
		.output(z.object({ success: z.literal(true) })),

	/** Toggle a playground document's enabled flag. Same guard as delete. */
	toggleEnabled: oc
		.route({
			method: "PATCH",
			path: "/api/playground/rag/documents/{id}/enabled",
			summary: "Toggle a playground document",
			tags: ["Playground"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
				enabled: z.boolean(),
			})
		)
		.output(RagDocumentSchema),

	/** Bulk-clear every playground document for this org. Convenience
	 *  knob for "start over" in the playground without nuking real docs. */
	clearAll: oc
		.route({
			method: "POST",
			path: "/api/playground/rag/documents/clear",
			summary: "Delete all playground documents for this org",
			tags: ["Playground"],
		})
		.output(
			z.object({
				deleted: z.number().int(),
			})
		),

	/** Inspect chunks for one playground document. */
	getChunks: oc
		.route({
			method: "GET",
			path: "/api/playground/rag/documents/{id}/chunks",
			summary: "List chunks for a playground document",
			tags: ["Playground"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
				limit: z.coerce.number().int().min(1).max(500).optional().default(100),
				cursor: z.coerce.number().int().min(0).optional().default(0),
			})
		)
		.output(
			z.object({
				documentId: z.number().int(),
				filename: z.string(),
				total: z.number().int(),
				chunks: z.array(PlaygroundChunkSchema),
			})
		),

	/**
	 * W5b — the standalone `search` procedure (POST
	 * `/api/playground/rag/search`) was DELETED. It was a second,
	 * semantic-only retrieval implementation that no longer matched what the
	 * playground agent actually runs: retrieval now goes through
	 * `RagSearchService` with `scope: 'playground'`, so the only way to observe
	 * a sandbox retrieval is the agent run itself (its `start` chunk carries
	 * `retrievedChunks`, and the model's `searchDocs` tool output carries the
	 * real hybrid results). `PlaygroundRerankingModeSchema` survives above
	 * because the barrel re-exports it.
	 */

	/**
	 * Run an agent invocation against the playground RAG index.
	 *
	 * The agent embeds the user prompt, retrieves top-K chunks from the
	 * playground store, then calls the chosen chat model with the system
	 * prompt + retrieved context + (optionally) prior turn history + user
	 * prompt. Surfaces every step so the playground can show the user exactly
	 * what was sent and received.
	 *
	 * `messages` carries prior conversation turns (excluding the new
	 * `userPrompt`). When `topK === 0`, retrieval is skipped — useful for
	 * comparing model behavior in isolation from any RAG corpus.
	 */
	agentRun: oc
		.route({
			method: "POST",
			path: "/api/playground/agent/run",
			summary: "Run an agent query with optional playground RAG context",
			tags: ["Playground"],
		})
		.input(
			z.object({
				userPrompt: z.string().trim().min(1).max(4000),
				systemPrompt: z.string().max(8000).optional(),
				agentModel: z.string().min(1),
				embeddingModel: z.string().optional(),
				threshold: z.number().min(0).max(1).optional(),
				topK: z.number().int().min(0).max(10).optional().default(5),
				includeDisabled: z.boolean().optional().default(true),
				messages: z
					.array(
						z.object({
							role: z.enum(["user", "assistant"]),
							content: z.string(),
						})
					)
					.max(50)
					.optional()
					.describe(
						"Prior turn history (oldest first), excluding the new userPrompt."
					),
				reasoningEffort: z
					.enum(["off", "low", "medium", "high"])
					.optional()
					.describe("Reasoning effort hint for reasoning-capable models."),
				verbosity: z
					.enum(["low", "medium", "high"])
					.optional()
					.describe("Verbosity hint forwarded as providerOptions.openrouter."),
				maxOutputTokens: z
					.number()
					.int()
					.min(1)
					.max(32_000)
					.nullable()
					.optional()
					.describe(
						"Cap on completion tokens. Falls back to a server default."
					),
				contextLimit: z
					.number()
					.int()
					.min(0)
					.max(50)
					.nullable()
					.optional()
					.describe(
						"Number of prior turns to keep (most recent). null/undefined = all."
					),
				attachments: z
					.array(
						z.object({
							filename: z.string().min(1).max(255),
							mediaType: z
								.string()
								.min(1)
								.describe(
									"MIME type — drives how the OpenRouter provider routes the part (image/* → image_url, audio/* → input_audio, else → file)."
								),
							url: z
								.string()
								.url()
								.describe("Fully-qualified URL of the uploaded attachment."),
						})
					)
					.max(8)
					.optional()
					.describe(
						"Files attached to the new user turn, in send order. Multimodal models receive these as FilePart content alongside the text prompt."
					),
				documentIds: z
					.array(z.number().int().min(1))
					.max(500)
					.optional()
					.describe(
						"Optional whitelist of playground doc IDs to restrict retrieval to. Each bench in the multi-panel playground passes its own subset so panels stay isolated."
					),
			})
		)
		.output(
			z.object({
				answer: z.string(),
				model: z.string(),
				retrievedChunks: z.array(
					z.object({
						documentId: z.number().int(),
						filename: z.string(),
						content: z.string(),
						similarity: z.number(),
						marker: z.string().optional(),
					})
				),
				finalPrompt: z
					.string()
					.describe(
						"The full prompt sent to the chat model, including retrieved context."
					),
				queryEmbeddingTokens: z.number().int(),
				promptTokens: z.number().int(),
				completionTokens: z.number().int(),
				totalTokens: z.number().int(),
				latencyMs: z.number().int(),
				warning: z.string().optional(),
			})
		),

	/**
	 * Streaming variant of {@link ragPlaygroundContract.agentRun}.
	 *
	 * Same retrieval + model invocation pipeline, but the model call uses
	 * `streamText` and emits tokens incrementally via an SSE event iterator
	 * so the playground UI can render the assistant reply as it arrives —
	 * mirroring the sidebar/fullscreen chat UX. The first chunk carries the
	 * retrieval payload (so the UI can immediately attach `retrievedChunks`
	 * to the placeholder turn); subsequent chunks are token deltas; a
	 * terminal `finish` chunk reports usage + total latency.
	 */
	agentRunStream: oc
		.route({
			method: "POST",
			path: "/api/playground/agent/run/stream",
			summary:
				"Streaming variant of agentRun. Yields token deltas via SSE so the playground can render the response as it arrives.",
			tags: ["Playground"],
		})
		.input(
			z.object({
				userPrompt: z.string().trim().min(1).max(4000),
				systemPrompt: z.string().max(8000).optional(),
				agentModel: z.string().min(1),
				embeddingModel: z.string().optional(),
				threshold: z.number().min(0).max(1).optional(),
				topK: z.number().int().min(0).max(10).optional().default(5),
				includeDisabled: z.boolean().optional().default(true),
				messages: z
					.array(
						z.object({
							role: z.enum(["user", "assistant"]),
							content: z.string(),
						})
					)
					.max(50)
					.optional(),
				reasoningEffort: z.enum(["off", "low", "medium", "high"]).optional(),
				verbosity: z.enum(["low", "medium", "high"]).optional(),
				maxOutputTokens: z
					.number()
					.int()
					.min(1)
					.max(32_000)
					.nullable()
					.optional(),
				contextLimit: z.number().int().min(0).max(50).nullable().optional(),
				attachments: z
					.array(
						z.object({
							filename: z.string().min(1).max(255),
							mediaType: z.string().min(1),
							url: z.string().url(),
						})
					)
					.max(8)
					.optional(),
				documentIds: z.array(z.number().int().min(1)).max(500).optional(),
				/**
				 * R7 — per-run answering-mode override. Sandbox-only: it never
				 * writes the org's `ragAgentMode`. Omitted ⇒ the run uses the
				 * org/preset value.
				 *
				 * Deliberately absent from `agentRun` (the NON-streaming twin):
				 * modes 4/5 produce a step tracker and turn-local citations, and
				 * the request→response output shape has nowhere honest to put
				 * either. Accepting the field there and ignoring it would be
				 * worse than not accepting it.
				 */
				agentMode: PlaygroundAgentModeSchema.optional(),
				/**
				 * Tools the playground operator has enabled for THIS bench. In
				 * advanced mode the user picks them per-panel; in standard mode
				 * the panel inherits the org config's enabled list. The server
				 * does NOT apply config-level structured-output gating — the
				 * playground is a sandbox, the operator is responsible for
				 * picking compatible model + tool combinations.
				 */
				enabledTools: z.array(z.string().min(1).max(64)).max(64).optional(),
				/**
				 * Snapshot of the panel's sandbox calendar. Sent on every
				 * request so calendar tools have a mutable scratch to operate
				 * against. Server-side mutations are emitted back as
				 * `tool-output-available` chunks; the client commits them into
				 * its store as the stream arrives. Capped to keep request
				 * bodies bounded; playground use rarely accrues this many
				 * events.
				 */
				calendarEvents: z
					.array(PlaygroundCalendarEventSchema)
					.max(500)
					.optional(),
				/**
				 * Tool-call IDs the operator has approved (or denied) since the
				 * previous turn. The server replays them as synthetic
				 * tool-result messages in `messages` so the agent can continue
				 * past an approval gate. The client is the source of truth for
				 * approval state.
				 */
				toolApprovals: z
					.array(
						z.object({
							toolCallId: z.string().min(1),
							toolName: z.string().min(1),
							input: z.unknown(),
							decision: z.enum(["approved", "denied"]),
						})
					)
					.max(64)
					.optional(),
			})
		)
		.output(eventIterator(PlaygroundAgentStreamChunkSchema)),
};

export type RagPlaygroundContract = typeof ragPlaygroundContract;
